/* ---------------------------------------------------------------------------
   The kinds of inspection a bridge has over its whole life, and which of them
   count towards a cycle. LIFECYCLE.md `LC-60` and `LC-61`.

   ONE LIST, BOTH SIDES. The API refuses a type or a trigger that is not here,
   the dialog offers exactly these, and the cycle in src/data/derive.ts reads
   `resetsCycle` and `satisfiesCycle` from here — so what a person can record,
   what the register stores and what the cycle counts cannot drift apart.

   SIX TYPES. The four the register always had, and two for the ends of a
   life that had none:
     · `baseline` — at handover (LC-11). The first full look at the structure
       as built, due on the handover date; it starts both cycles.
     · `post-event` — after a flood, an earthquake, an impact or an overload,
       carrying the event that caused it. `special` stays for an inspection
       asked for on a concern rather than an event, and records its trigger too.

   WHAT RESETS WHICH CLOCK (LC-61). A principal inspection is a routine one
   and more: the close-up visit sees everything the walk-over would, so it
   resets the routine clock as well as its own. A baseline is a principal
   inspection of a new structure and resets both. Special, post-event and
   underwater inspections look at one thing for one reason and reset neither.

   ONLY WHAT WAS SIGNED OFF COUNTS. A draft, one waiting in review, and one
   still on the device (`pending`) are all on the record, and none of them is
   the register's claim that the structure was inspected. An entry with no
   state predates the workflow and has always been treated as on file; it
   still is.
   --------------------------------------------------------------------------- */

import type { Inspection, InspectionTrigger, InspectionType } from './types.ts';

export const INSPECTION_TYPES: readonly InspectionType[] = [
  'routine',
  'principal',
  'baseline',
  'special',
  'post-event',
  'underwater',
];

export function isInspectionType(raw: unknown): raw is InspectionType {
  return typeof raw === 'string' && (INSPECTION_TYPES as readonly string[]).includes(raw);
}

export const INSPECTION_TRIGGERS: readonly { id: InspectionTrigger; label: string }[] = [
  { id: 'flood', label: 'Flood' },
  { id: 'earthquake', label: 'Earthquake' },
  { id: 'impact', label: 'Impact' },
  { id: 'overload', label: 'Overload' },
  /* Works completed on a bridge in service (LC-82): the special inspection
     the completion makes due. */
  { id: 'works', label: 'Works completed' },
  { id: 'other', label: 'Other' },
];

export function isInspectionTrigger(raw: unknown): raw is InspectionTrigger {
  return typeof raw === 'string' && INSPECTION_TRIGGERS.some((t) => t.id === raw);
}

export function triggerLabel(trigger: InspectionTrigger): string {
  return INSPECTION_TRIGGERS.find((t) => t.id === trigger)?.label ?? trigger;
}

/** The types that name what caused them. */
export function needsTrigger(type: InspectionType): boolean {
  return type === 'special' || type === 'post-event';
}

/** The cycles an inspection of this type resets. */
export function resetsCycle(type: InspectionType, cycle: 'routine' | 'principal'): boolean {
  if (type === 'baseline') return true;
  if (type === 'principal') return true;
  return cycle === 'routine' && type === 'routine';
}

/** Whether an entry is the register's claim that the structure was inspected. */
export function satisfiesCycle(insp: Pick<Inspection, 'state' | 'pending'>): boolean {
  if (insp.pending === true) return false;
  return insp.state === undefined || insp.state === 'accepted';
}

/**
 * A department's inspection intervals, in months (LC-61), and when they are
 * shortened. PROVISIONAL in every profile, as the re-rating interval is.
 *
 * `poorFrom` is where condition turns poor, as a fraction of the scale's top —
 * condition runs 0 good to the top worst on every form this register holds —
 * so 0.75 is 3 on DoR's 0–4.
 */
export interface InspectionIntervals {
  routineMonths: number;
  principalMonths: number;
  /** For a restricted bridge, or one whose latest condition is poor. */
  shortRoutineMonths: number;
  shortPrincipalMonths: number;
  poorFrom: number;
}
